import { useEffect, useState, type ReactNode } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

export type ReviewSlide = { key: string; node: ReactNode };

/**
 * One-at-a-time vertical slider used for review cards on the home page and
 * every product page. Auto-advances, pauses on hover/focus and honours
 * prefers-reduced-motion.
 */
export function VerticalReviewSlider({
  slides,
  height = 200,
  intervalMs = 5000,
  className = "",
}: {
  slides: ReviewSlide[];
  height?: number;
  intervalMs?: number;
  className?: string;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = slides.length;

  useEffect(() => {
    if (index > total - 1) setIndex(0);
  }, [total, index]);

  useEffect(() => {
    if (paused || total < 2) return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % total), intervalMs);
    return () => window.clearInterval(id);
  }, [paused, total, intervalMs]);

  if (total === 0) return null;
  const go = (delta: number) => setIndex((i) => (i + delta + total) % total);

  return (
    <div
      className={className}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="relative overflow-hidden" style={{ height }}>
        <div
          className="transition-transform duration-500 ease-out"
          style={{ transform: `translateY(-${index * height}px)` }}
        >
          {slides.map((s) => (
            <div key={s.key} style={{ height }} className="pb-2">
              <div className="h-full overflow-y-auto">{s.node}</div>
            </div>
          ))}
        </div>
      </div>

      {total > 1 && (
        <div className="mt-2 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous review"
            className="rounded-full border border-[#D9D9D6] bg-white p-1.5 text-[#2C2C2C] hover:bg-[#F7F7F5]"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show review ${i + 1}`}
                aria-current={i === index}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-5 bg-[#1E5EFF]" : "w-1.5 bg-[#D9D9D6]"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next review"
            className="rounded-full border border-[#D9D9D6] bg-white p-1.5 text-[#2C2C2C] hover:bg-[#F7F7F5]"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
